import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { 
  Save, 
  Loader2, 
  Plus, 
  Trash2,
  Eye,
  FileText
} from "lucide-react";
import { toast } from "sonner";

export default function AdminLandingPage() {
  const { token, loading: authLoading } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    headline: "A Remixed Way to Reinforce Black Letter Law",
    headline_highlight: "Remixed",
    subheadline: "Structured song-form drills designed to supplement traditional law school study.",
    bullets: [
      "Reinforce rules while commuting",
      "Review black letter law through repetition",
      "Designed by a 20+ year legal professional, songwriter, and current law student"
    ],
    button_text: "Get the Free Audio Sample",
    success_message: "You're on the list!",
    sample_description: "Here's your free sample. Enjoy!",
    footer_message: "We'll notify you when Bar Songz launches!"
  });

  useEffect(() => {
    // Fetch config only when auth is ready and we have a token
    if (!authLoading && token) {
      fetchConfig();
    } else if (!authLoading && !token) {
      // Auth done but no token - show page with defaults
      setLoading(false);
    }
  }, [token, authLoading]);

  const fetchConfig = async () => {
    // Guard: don't fetch without token
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/landing-page-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Config doesn't exist yet, use defaults
      console.log("Using default landing page config");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/landing-page-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Landing page updated successfully!");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const addBullet = () => {
    setConfig(prev => ({
      ...prev,
      bullets: [...prev.bullets, ""]
    }));
  };

  const updateBullet = (index, value) => {
    setConfig(prev => ({
      ...prev,
      bullets: prev.bullets.map((b, i) => i === index ? value : b)
    }));
  };

  const removeBullet = (index) => {
    setConfig(prev => ({
      ...prev,
      bullets: prev.bullets.filter((_, i) => i !== index)
    }));
  };

  // Show loading only while auth is being checked
  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="admin-landing-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  // Show content loading state after auth is ready
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="admin-landing-content-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-landing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="page-title">Landing Page Settings</h1>
          <p className="text-muted-foreground">Customize the gated landing page content</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open("/", "_blank")}
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-gold text-primary-foreground hover:bg-gold/90"
            data-testid="save-changes-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Headline Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Headline
          </CardTitle>
          <CardDescription>
            The main headline displayed at the top of the landing page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Headline Text</Label>
            <Input
              value={config.headline}
              onChange={(e) => setConfig(prev => ({ ...prev, headline: e.target.value }))}
              placeholder="A Remixed Way to Reinforce Black Letter Law"
              data-testid="headline-input"
            />
            <p className="text-xs text-muted-foreground">
              The full headline text
            </p>
          </div>
          <div className="space-y-2">
            <Label>Highlighted Word (shown in gold italics)</Label>
            <Input
              value={config.headline_highlight}
              onChange={(e) => setConfig(prev => ({ ...prev, headline_highlight: e.target.value }))}
              placeholder="Remixed"
              data-testid="headline-highlight-input"
            />
            <p className="text-xs text-muted-foreground">
              This word will appear in gold and italics within the headline
            </p>
          </div>
          <div className="space-y-2">
            <Label>Sub-headline</Label>
            <Textarea
              value={config.subheadline}
              onChange={(e) => setConfig(prev => ({ ...prev, subheadline: e.target.value }))}
              placeholder="Structured song-form drills designed to supplement traditional law school study."
              rows={2}
              data-testid="subheadline-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bullet Points Section */}
      <Card>
        <CardHeader>
          <CardTitle>Benefit Bullets</CardTitle>
          <CardDescription>
            Key benefits displayed with icons below the headline
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {config.bullets.map((bullet, index) => (
            <div key={index} className="flex items-center gap-2" data-testid={`bullet-row-${index}`}>
              <span className="text-muted-foreground text-sm w-6">{index + 1}.</span>
              <Input
                value={bullet}
                onChange={(e) => updateBullet(index, e.target.value)}
                placeholder="Enter benefit text..."
                className="flex-1"
                data-testid={`bullet-input-${index}`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => removeBullet(index)}
                disabled={config.bullets.length <= 1}
                data-testid={`bullet-delete-${index}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={addBullet}
            className="mt-2"
            data-testid="add-bullet-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Bullet
          </Button>
        </CardContent>
      </Card>

      {/* Button & Messages Section */}
      <Card>
        <CardHeader>
          <CardTitle>Button & Messages</CardTitle>
          <CardDescription>
            Customize the call-to-action button and success messages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Button Text</Label>
            <Input
              value={config.button_text}
              onChange={(e) => setConfig(prev => ({ ...prev, button_text: e.target.value }))}
              placeholder="Get the Free Audio Sample"
              data-testid="button-text-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Success Message (after email submission)</Label>
            <Input
              value={config.success_message}
              onChange={(e) => setConfig(prev => ({ ...prev, success_message: e.target.value }))}
              placeholder="You're on the list!"
              data-testid="success-message-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Sample Description</Label>
            <Input
              value={config.sample_description}
              onChange={(e) => setConfig(prev => ({ ...prev, sample_description: e.target.value }))}
              placeholder="Here's your free sample. Enjoy!"
              data-testid="sample-description-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Footer Message</Label>
            <Input
              value={config.footer_message}
              onChange={(e) => setConfig(prev => ({ ...prev, footer_message: e.target.value }))}
              placeholder="We'll notify you when Bar Songz launches!"
              data-testid="footer-message-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
